// Mock-only archive reads, shaped exactly like services/api/src/document-archive
// so the screen runs unchanged against the live door.
//
// Two things the mock must NOT do, because they would hide the bugs the live
// tests exist to catch:
//   • it must not put a `path`, folder or container on an item — the real API
//     strips storage layout at the boundary;
//   • it must not pretend an unarchived document is readable. Only the one item
//     marked available resolves; everything else answers 409, which is what the
//     live route does until the archiving step has run.
import type { MockRoute } from "./slice-a";

const json = (data: unknown, status = 200) => new Response(JSON.stringify(data), { status, headers: { "content-type": "application/json" } });
const ok = (data: unknown) => json({ ok: true, data });

/** The doors the client should render, as the config would supply them. */
const ENTRIES = [
  { source: "po_documents", label: "Purchase orders", entry_surface: "crm_job_po", entry_label: "Documents" },
  { source: "installer_photos", label: "Installer photos", entry_surface: "installer_completion", entry_label: "Photos" },
] as const;

const id = (s: string) => s.padEnd(32, "0").slice(0, 32).replace(/[^a-f0-9]/g, "a");

type Item = {
  id: string; source: "po_documents" | "installer_photos"; title: string; detail: string;
  reference: string | null; group_key: string; group_label: string;
  recorded_at: string | null; available: boolean;
  customer: string | null; order_number: string | null;
};

const po = (over: Partial<Item> & { id: string }): Item => ({
  source: "po_documents", title: "Purchase order", detail: "Wonderwood · $4120.00 ex GST · confirmed",
  reference: "J072610164", group_key: "Wonderwood", group_label: "Wonderwood",
  recorded_at: "2026-09-03T01:00:00Z", available: false,
  customer: "Nguyen — Spice St", order_number: "AIW-A1B2C3D4E5F6-V1", ...over,
});

const photo = (over: Partial<Item> & { id: string }): Item => ({
  source: "installer_photos", title: "Completion photo", detail: "J072610164 · completion",
  reference: "J072610164", group_key: "2026-09", group_label: "September 2026",
  recorded_at: "2026-09-04T05:00:00Z", available: true,
  customer: "Nguyen — Spice St", order_number: null, ...over,
});

const PO_ITEMS: Item[] = [
  po({ id: id("a1"), title: "Purchase order · J072610164", reference: "J072610164", group_key: "Wonderwood", group_label: "Wonderwood", detail: "Wonderwood · $4120.00 ex GST · confirmed" }),
  po({ id: id("a2"), title: "Purchase order · J072610164", reference: "J072610164", group_key: "Wonderwood", group_label: "Wonderwood", detail: "Wonderwood · $1880.00 ex GST · drafted", recorded_at: "2026-09-01T01:00:00Z" }),
  po({ id: id("a3"), title: "Purchase order · J072610155", reference: "J072610155", group_key: "National Supply", group_label: "National Supply", detail: "National Supply · $2880.00 ex GST · submitted", recorded_at: "2026-09-01T01:00:00Z" }),
];

const PHOTO_ITEMS: Item[] = [
  photo({ id: id("b1"), title: "Completion photo · J072610164", reference: "J072610164", group_key: "2026-09", group_label: "September 2026" }),
  photo({ id: id("b2"), title: "Completion photo · J072610141", reference: "J072610141", group_key: "2026-08", group_label: "August 2026", recorded_at: "2026-08-28T05:00:00Z" }),
  photo({ id: id("b3"), title: "Completion photo · J072610141", reference: "J072610141", group_key: "2026-08", group_label: "August 2026", recorded_at: "2026-08-28T06:00:00Z" }),
];

/** Group and order exactly as browseArchive does: newest first inside a group,
 *  months newest-first, suppliers A→Z. */
function groupsFor(items: Item[], jobRef: string | null) {
  const scoped = jobRef ? items.filter((i) => i.reference === jobRef) : items;
  const bySource = new Map<string, Map<string, Item[]>>();
  for (const i of scoped) {
    if (!bySource.has(i.source)) bySource.set(i.source, new Map());
    const g = bySource.get(i.source)!;
    if (!g.has(i.group_key)) g.set(i.group_key, []);
    g.get(i.group_key)!.push(i);
  }
  const out: Record<string, unknown>[] = [];
  for (const source of ENTRIES) {
    const g = bySource.get(source.source);
    if (!g) continue;
    const months = source.source === "installer_photos";
    const keys = [...g.keys()].sort((a, b) => (months ? b.localeCompare(a) : a.localeCompare(b)));
    for (const key of keys) {
      const rows = [...g.get(key)!].sort((a, b) => (b.recorded_at ?? "").localeCompare(a.recorded_at ?? "") || a.id.localeCompare(b.id));
      out.push({
        source: source.source,
        label: rows[0]!.group_label,
        group_by: months ? "month_completed" : "supplier",
        entry_surface: source.entry_surface,
        entry_label: source.entry_label,
        items: rows,
      });
    }
  }
  return out;
}

export const archiveSlice: MockRoute = (path, q) => {
  if (path === "/api/archive") {
    const job = q.get("job");
    const groups = groupsFor([...PO_ITEMS, ...PHOTO_ITEMS], job);
    return ok({ entries: ENTRIES.map((e) => ({ ...e })), groups });
  }
  const file = path.match(/^\/api\/archive\/([a-f0-9]{32})\/file$/);
  if (file) {
    const wanted = file[1]!;
    const hit = [...PO_ITEMS, ...PHOTO_ITEMS].find((i) => i.id === wanted);
    if (!hit) return json({ error: "Document not found in this workspace" }, 404);
    // Available means the bytes exist; anything else must say so rather than
    // stream an empty file and look like it worked.
    if (!hit.available) return json({ error: "This document is listed but has no archived copy yet." }, 409);
    // A one-page PDF, so the open path is genuinely exercised in mock mode.
    const pdf = "%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj\n3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 200 100]>>endobj\ntrailer<</Root 1 0 R>>\n%%EOF\n";
    return new Response(pdf, { status: 200, headers: { "content-type": "application/pdf" } });
  }
  return null;
};
